import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AUTHORIZED_EMAILS = ["thornton.carlton68@gmail.com"];

const SEGMENT_COLORS = {
  internal:    { bg: '#eef2ff', text: '#4f46e5', border: '#c7d2fe' },
  admin:       { bg: '#1e3a5f', text: '#fff',    border: '#1e3a5f' },
  tester:      { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' },
  influencer:  { bg: '#f5f3ff', text: '#7c3aed', border: '#ddd6fe' },
  mindfulness: { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' },
  nonprofit:   { bg: '#eff6ff', text: '#1e3a5f', border: '#bfdbfe' },
  hcsm:        { bg: '#eef2ff', text: '#4f46e5', border: '#c7d2fe' },
  ebs:         { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' },
  hrm:         { bg: '#fef3c7', text: '#b45309', border: '#fde68a' },
  insureco:    { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  peo:         { bg: '#fdf4ff', text: '#7e22ce', border: '#e9d5ff' },
  rehab:       { bg: '#fff7ed', text: '#c2410c', border: '#fed7aa' },
  chaplain:    { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
  ltc:         { bg: '#f0fdfa', text: '#0d9488', border: '#99f6e4' },
};

const COACH_LABELS = {
  emit: '✨ Emit/SAGE',
  safe: '🛡️ SAFE',
  family_coach: '👨‍👩‍👧 Family',
  mental_performance: '⚡ Performance',
};

const T = {
  pageBg: '#f0f4f8', navy: '#1e3a5f', heading: '#1e293b',
  body: '#475569', bodyLight: '#64748b', accent: '#4f46e5',
  accentLight: '#eef2ff', accentBorder: '#c7d2fe',
  green: '#059669', greenLight: '#ecfdf5', greenBorder: '#a7f3d0',
  cardBg: '#ffffff', cardBorder: '#e2e8f0',
};

function formatDate(str) {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [status, setStatus] = useState('loading');
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [resetPw, setResetPw] = useState({ partnerId: null, partnerName: '', password: '', status: '' });
  const [newPartner, setNewPartner] = useState({ name: '', email: '', code: '', type: 'org', segment: '', notes: '' });
  const [createStatus, setCreateStatus] = useState('');
  const apiUrl = window.location.origin;

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    let userEmail = '';
    try { userEmail = storedUser ? JSON.parse(storedUser).email : ''; } catch { userEmail = ''; }
    if (!userEmail || !AUTHORIZED_EMAILS.map(e => e.toLowerCase()).includes(userEmail.toLowerCase())) {
      setStatus('denied'); return;
    }
    fetch(`${apiUrl}/api/v1/partner-dashboard`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { if (r.status === 403) { setStatus('denied'); return null; } if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { if (!data) return; setPartners(data.partners || []); setStatus('ready'); })
      .catch(() => setStatus('error'));
  }, []);

  const totalMembers = partners.reduce((s, p) => s + (p.total_members || 0), 0);
  const totalSessions = partners.reduce((s, p) => s + (p.total_sessions || 0), 0);
  const activePartners = partners.filter(p => p.active && p.total_members > 0).length;

  const handleCreate = async () => {
    const storedUser = localStorage.getItem('user');
    let userEmail = '';
    try { userEmail = storedUser ? JSON.parse(storedUser).email : ''; } catch {}
    if (!newPartner.name || !newPartner.code) { setCreateStatus('Name and code are required.'); return; }
    setCreateStatus('saving');
    const params = new URLSearchParams({ name: newPartner.name, email: newPartner.email, code: newPartner.code, ptype: newPartner.type, segment: newPartner.segment, notes: newPartner.notes });
    const r = await fetch(`${apiUrl}/api/v1/partners/create?${params}`, { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
    if (r.ok) { setCreateStatus('saved'); setShowCreate(false); setNewPartner({ name: '', email: '', code: '', type: 'org', segment: '', notes: '' }); window.location.reload(); }
    else { setCreateStatus('error'); }
  };

  const handleResetPw = async () => {
    if (!resetPw.password || resetPw.password.length < 8) { setResetPw({ ...resetPw, status: 'Password must be at least 8 characters.' }); return; }
    const storedUser = localStorage.getItem('user');
    let userEmail = '';
    try { userEmail = storedUser ? JSON.parse(storedUser).email : ''; } catch {}
    setResetPw({ ...resetPw, status: 'saving' });
    const r = await fetch(`${window.location.origin}/api/v1/partner-reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ partner_id: resetPw.partnerId, new_password: resetPw.password })
    });
    if (r.ok) { setResetPw({ partnerId: null, partnerName: '', password: '', status: '' }); }
    else { const d = await r.json(); setResetPw({ ...resetPw, status: d.detail || 'Failed.' }); }
  };

  if (status === 'loading') return <div style={{ minHeight: '100vh', background: T.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.body }}>Loading...</div>;
  if (status === 'denied') return <div style={{ minHeight: '100vh', background: T.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.body }}>Access denied.</div>;
  if (status === 'error') return <div style={{ minHeight: '100vh', background: T.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.body }}>Error loading data.</div>;

  return (
    <div style={{ minHeight: '100vh', background: T.pageBg, fontFamily: "'Libre Franklin', sans-serif" }}>

      {/* Header */}
      <div style={{ background: T.navy, padding: '20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '4px' }}>FHO PLATFORM</div>
          <div style={{ fontSize: '22px', fontWeight: '700', color: '#fff' }}>Partner Dashboard</div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => setShowCreate(true)} style={{ padding: '9px 20px', background: '#4f46e5', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>+ New Partner Code</button>
          <button onClick={() => navigate('/leads')} style={{ padding: '9px 20px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>📋 Leads</button>
          <button onClick={() => navigate('/journey')} style={{ padding: '9px 20px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>← Dashboard</button>
        </div>
      </div>

      {/* Stats Bar */}
      <div style={{ background: '#fff', borderBottom: `1px solid ${T.cardBorder}`, padding: '20px 32px', display: 'flex', gap: '48px', flexWrap: 'wrap' }}>
        {[
          { label: 'Total Partner Codes', value: partners.length },
          { label: 'Total Members', value: totalMembers },
          { label: 'Total Sessions', value: totalSessions },
          { label: 'Active Partners', value: activePartners },
        ].map(s => (
          <div key={s.label}>
            <div style={{ fontSize: '28px', fontWeight: '700', color: T.navy }}>{s.value}</div>
            <div style={{ fontSize: '12px', color: T.bodyLight, marginTop: '2px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>

        {/* Partner Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '20px' }}>
          {partners.map(p => {
            const colors = SEGMENT_COLORS[p.segment] || SEGMENT_COLORS[p.type] || SEGMENT_COLORS.tester;
            const isSelected = selected?.id === p.id;
            return (
              <div key={p.id}
                onClick={() => setSelected(isSelected ? null : p)}
                style={{ background: T.cardBg, borderRadius: '16px', border: `1px solid ${isSelected ? T.accent : T.cardBorder}`, boxShadow: isSelected ? `0 0 0 2px ${T.accent}` : '0 2px 12px rgba(0,0,0,0.05)', cursor: 'pointer', overflow: 'hidden', transition: 'all 0.2s' }}>
                <div style={{ padding: '20px 22px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: T.heading, marginBottom: '4px' }}>{p.name}</div>
                      <div style={{ fontSize: '12px', color: T.bodyLight }}>{p.email || '—'}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                      <div style={{ padding: '3px 10px', borderRadius: '20px', background: colors.bg, border: `1px solid ${colors.border}`, fontSize: '11px', fontWeight: '700', color: colors.text, letterSpacing: '0.06em' }}>{p.segment || p.type}</div>
                      <div style={{ padding: '2px 8px', borderRadius: '6px', background: p.active ? T.greenLight : '#fef2f2', border: `1px solid ${p.active ? T.greenBorder : '#fecaca'}`, fontSize: '10px', fontWeight: '700', color: p.active ? T.green : '#dc2626' }}>{p.active ? 'ACTIVE' : 'INACTIVE'}</div>
                    </div>
                  </div>

                  {/* Code */}
                  <div style={{ padding: '8px 12px', background: T.pageBg, borderRadius: '8px', fontFamily: 'monospace', fontSize: '14px', fontWeight: '700', color: T.accent, letterSpacing: '0.08em', marginBottom: '16px' }}>{p.code}</div>

                  {/* Stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {[
                      { label: 'Members', value: p.total_members || 0, color: T.accent },
                      { label: 'Sessions', value: p.total_sessions || 0, color: T.green },
                      { label: 'Active 30d', value: p.active_members_30d || 0, color: '#d97706' },
                    ].map(s => (
                      <div key={s.label} style={{ textAlign: 'center', padding: '10px 8px', background: T.pageBg, borderRadius: '8px' }}>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: '10px', color: T.bodyLight, marginTop: '2px' }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Expanded detail */}
                {isSelected && (
                  <div style={{ borderTop: `1px solid ${T.cardBorder}`, padding: '16px 22px', background: T.pageBg }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: T.bodyLight, letterSpacing: '0.06em', marginBottom: '10px', textTransform: 'uppercase' }}>Sessions by Coach</div>
                    {Object.keys(p.sessions_by_coach || {}).length === 0
                      ? <div style={{ fontSize: '13px', color: T.bodyLight }}>No sessions yet</div>
                      : Object.entries(p.sessions_by_coach).map(([coach, count]) => (
                        <div key={coach} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${T.cardBorder}` }}>
                          <span style={{ fontSize: '13px', color: T.body }}>{COACH_LABELS[coach] || coach}</span>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: T.navy }}>{count} sessions</span>
                        </div>
                      ))
                    }
                    <div style={{ marginTop: '10px', fontSize: '12px', color: T.bodyLight }}>
                      Last signup: {formatDate(p.last_signup)} · Created: {formatDate(p.created_at)}
                    </div>
                    {p.notes && <div style={{ marginTop: '8px', fontSize: '12px', color: T.bodyLight, fontStyle: 'italic' }}>{p.notes}</div>}
                    <button onClick={e => { e.stopPropagation(); setResetPw({ partnerId: p.id, partnerName: p.name, password: '', status: '' }); }}
                      style={{ marginTop: '12px', padding: '8px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                      🔑 Reset Password
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Reset Password Modal */}
      {resetPw.partnerId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 8px', color: T.navy, fontSize: '20px' }}>Reset Password</h2>
            <p style={{ fontSize: '13px', color: T.bodyLight, margin: '0 0 20px' }}>Set a new password for <strong>{resetPw.partnerName}</strong></p>
            <label style={{ fontSize: '11px', fontWeight: '700', color: T.bodyLight, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>New Password</label>
            <input type="password" value={resetPw.password} onChange={e => setResetPw({ ...resetPw, password: e.target.value })}
              placeholder="Min. 8 characters"
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, fontSize: '14px', boxSizing: 'border-box', outline: 'none', marginBottom: '16px' }} />
            {resetPw.status && resetPw.status !== 'saving' && <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '12px' }}>{resetPw.status}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setResetPw({ partnerId: null, partnerName: '', password: '', status: '' })}
                style={{ padding: '10px 20px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, background: '#fff', color: T.body, fontSize: '14px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleResetPw} disabled={resetPw.status === 'saving'}
                style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#dc2626', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                {resetPw.status === 'saving' ? 'Saving...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Partner Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 24px', color: T.navy, fontSize: '20px' }}>New Partner Code</h2>
            {[
              { label: 'Partner Name *', key: 'name', placeholder: 'Resourcing Edge PEO' },
              { label: 'Contact Email', key: 'email', placeholder: 'contact@example.com' },
              { label: 'Access Code *', key: 'code', placeholder: 'FHO-RESOURCING', mono: true },
              { label: 'Notes', key: 'notes', placeholder: 'Optional notes' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '11px', fontWeight: '700', color: T.bodyLight, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>{f.label}</label>
                <input value={newPartner[f.key]} onChange={e => setNewPartner({ ...newPartner, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, fontSize: '14px', fontFamily: f.mono ? 'monospace' : 'inherit', textTransform: f.mono ? 'uppercase' : 'none', boxSizing: 'border-box', outline: 'none' }} />
              </div>
            ))}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: T.bodyLight, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Type</label>
              <select value={newPartner.type} onChange={e => setNewPartner({ ...newPartner, type: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, fontSize: '14px', boxSizing: 'border-box' }}>
                <option value="org">Organization</option>
                <option value="influencer">Influencer</option>
                <option value="tester">Tester</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: T.bodyLight, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>Segment</label>
              <select value={newPartner.segment} onChange={e => setNewPartner({ ...newPartner, segment: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, fontSize: '14px', boxSizing: 'border-box' }}>
                <option value="">Select segment</option>
                <option value="hcsm">HCSM</option>
                <option value="ebs">EBS</option>
                <option value="hrm">HRM</option>
                <option value="insureco">InsureCo</option>
                <option value="peo">PEO</option>
                <option value="rehab">Rehab</option>
                <option value="chaplain">Chaplain</option>
                <option value="ltc">LTC</option>
                <option value="influencer">Influencer</option>
                <option value="mindfulness">Mindfulness</option>
                <option value="nonprofit">Nonprofit</option>
                <option value="internal">Internal</option>
              </select>
            </div>
            {createStatus === 'error' && <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '12px' }}>Failed to create. Check all fields.</div>}
            {createStatus && createStatus !== 'error' && createStatus !== 'saving' && <div style={{ color: T.green, fontSize: '13px', marginBottom: '12px' }}>{createStatus}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreate(false)} style={{ padding: '10px 20px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, background: '#fff', color: T.body, fontSize: '14px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCreate} disabled={createStatus === 'saving'} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: T.accent, color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>{createStatus === 'saving' ? 'Saving...' : 'Create Code'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerDashboard;
